import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { InputNumberMask } from '../../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Label } from '../../../../../../../../CommonComponents/OPL/Label/Label';
import { Text } from '../../../../../../../../CommonComponents/OPL/Text/Text';
import { formatCurrency } from '../../../../../../../../CommonUtils/formatNumber';
import { ACCOUNTS_WITH_HOLDINGS_TAB, AnnuityType as AnnuityTypes } from '../../AccountForm.meta';
import { parsePositionValue } from '../../utils/parsePositionValue';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { useAccountFormContext } from '../../context/useAccountFormContext';

interface BalanceProps {
  label?: string;
}

export const Balance = ({ label = 'Balance' }: BalanceProps) => {
  const formBag = useFormContext();

  const { setAlert } = useAccountFormContext();

  const { watch } = formBag;

  const [
    accountType,
    specificPositionsValue,
    nonSpecificPositionsValue,
    isLinked,
    isIntegrated,
    syncAccountValue,
    annuityType,
  ] = watch([
    'bankAccount.AccountType',
    'bankAccount.SpecificPositionsValue',
    'bankAccount.NonSpecificPositionsValue',
    'bankAccount.IsLinked',
    'bankAccount.IsIntegrated',
    'bankAccount.SyncAccountValueEnabled',
    'bankAccount.AnnuityType',
  ]);

  const showMarketValueBreakdown =
    ACCOUNTS_WITH_HOLDINGS_TAB.includes(accountType) &&
    parsePositionValue(specificPositionsValue) > 0 &&
    annuityType !== AnnuityTypes.Fixed;

  return (
    <>
      {showMarketValueBreakdown && (
        <Controller
          control={formBag.control}
          name="bankAccount.SpecificPositionsValue"
          render={({ field }) => (
            <InputNumberMask
              {...field}
              disabled
              leftIcon="fa fa-dollar-sign"
              label="Specific Holdings"
              allowNegative={false}
              decimalScale={2}
            />
          )}
        />
      )}

      <Controller
        control={formBag.control}
        name="bankAccount.NonSpecificPositionsValue"
        render={({ field: { onChange, ...rest } }) => (
          <InputNumberMask
            {...rest}
            leftIcon="fa fa-dollar-sign"
            label={showMarketValueBreakdown ? 'Non-specific Holdings' : label}
            allowNegative={false}
            decimalScale={2}
            onValueChange={({ floatValue }) => {
              if ((isLinked || isIntegrated) && syncAccountValue) {
                setAlert({
                  message:
                    'Changing your asset value will automatically un-sync asset value for this account. To re-sync, toggle the sync asset value toggle in the sync tab',
                  type: 'warning',
                });

                formBag.setValue('bankAccount.SyncAccountValueEnabled', false);
              }

              onChange(floatValue ?? null);
            }}
          />
        )}
      />

      {showMarketValueBreakdown && (
        <Box>
          <Label mb="4">Total Current Market Value</Label>

          <Text fontWeight={500} variant="tabular">
            {formatCurrency(
              parsePositionValue(specificPositionsValue) +
                parsePositionValue(nonSpecificPositionsValue),
            )}
          </Text>
        </Box>
      )}
    </>
  );
};
